import * as THREE from 'three';
import { SAFE_ZONE_VERTEX_CHUNK, safeZoneUniforms } from '../../shaders/safeZone.glsl.js';

// THE MOVING HEADS IN THE HAZE. Each lyre is a cone of light whose brightness
// is the haze's DENSITY where the ray passes (a drifting noise in world
// space), times a soft profile across the beam (strong where the cone's
// surface faces the camera: the centre of the beam, seen through the most
// air), times its fall-off along the length. Never a flat triangle. The cones
// are drawn at HALF resolution into a HalfFloat target and composited back,
// with a faint haze layer lit by the screens around the stage; the lens of
// each head is a sharp glare at full resolution.

export const HEADS_MAX = 16;
const LEN = 42;

function noiseTexture() {
  const S = 128, d = new Uint8Array(S * S * 4);
  // tileable value noise, three octaves
  const g = (n) => { const a = new Float32Array(n * n); for (let i = 0; i < n * n; i++) a[i] = Math.random(); return a; };
  const oct = [[8, g(8)], [16, g(16)], [32, g(32)]];
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    let v = 0, w = 0.55;
    for (const [n, a] of oct) {
      const fx = x / S * n, fy = y / S * n, ix = Math.floor(fx), iy = Math.floor(fy), tx = fx - ix, ty = fy - iy;
      const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
      const at = (i, j) => a[((j % n + n) % n) * n + ((i % n + n) % n)];
      v += w * ((at(ix, iy) * (1 - sx) + at(ix + 1, iy) * sx) * (1 - sy) + (at(ix, iy + 1) * (1 - sx) + at(ix + 1, iy + 1) * sx) * sy);
      w *= 0.5;
    }
    const o = (y * S + x) * 4;
    d[o] = d[o + 1] = d[o + 2] = Math.round(Math.min(1, v / 0.96) * 255); d[o + 3] = 255;
  }
  const t = new THREE.DataTexture(d, S, S);
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.magFilter = t.minFilter = THREE.LinearFilter; t.needsUpdate = true;
  return t;
}

const coneVertex = `
  precision highp float;
  attribute float aHead;
  uniform vec3 uO[${HEADS_MAX}];
  uniform vec3 uD[${HEADS_MAX}];
  uniform vec4 uC[${HEADS_MAX}];     // linear colour, level
  uniform float uSpread[${HEADS_MAX}];
  uniform float uN;
  varying vec3 vW;
  varying vec3 vN;
  varying float vT;
  varying vec4 vC;
  void main() {
    int i = int(aHead + 0.5);
    vec3 O = vec3(0.0), D = vec3(0.0, -1.0, 0.0); vec4 C = vec4(0.0); float sp = 0.1;
    for (int k = 0; k < ${HEADS_MAX}; k++) if (k == i) { O = uO[k]; D = uD[k]; C = uC[k]; sp = uSpread[k]; }
    if (aHead >= uN || C.w < 0.002) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float t = 0.5 - position.y;                      // 0 at the head, 1 at the end
    vec3 a = abs(D.y) < 0.95 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 p1 = normalize(cross(D, a)), p2 = cross(D, p1);
    vec2 r2 = normalize(position.xz + 1e-6);
    float rad = 0.14 + sp * t * ${LEN.toFixed(1)};
    vec3 n = p1 * r2.x + p2 * r2.y;
    vec3 w = O + D * (t * ${LEN.toFixed(1)}) + n * rad;
    vW = w; vN = n; vT = t; vC = C;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;
const coneFragment = `
  precision highp float;
  uniform sampler2D uNoise;
  uniform float uTime;
  uniform float uHaze;               // overall haze amount
  varying vec3 vW; varying vec3 vN; varying float vT; varying vec4 vC;
  float haze(vec3 w) {
    float a = texture2D(uNoise, w.xz * 0.035 + vec2(uTime * 0.012, 0.0)).r;
    float b = texture2D(uNoise, w.xy * 0.05 + vec2(0.0, -uTime * 0.009)).r;
    float hgt = smoothstep(-1.0, 3.0, w.y) * (1.0 - 0.4 * smoothstep(14.0, 24.0, w.y));
    float n = a * b;
    return (0.08 + 2.6 * n * n) * hgt;
  }
  void main() {
    vec3 v = normalize(cameraPosition - vW);
    float facing = abs(dot(vN, v));
    // the soft profile across the beam: bright through its middle, fading at its edges
    float prof = facing * facing * facing;
    float along = exp(-vT * 2.2) * (1.0 - smoothstep(0.75, 1.0, vT));
    // the camera inside or along the cone: no smear
    float inside = smoothstep(0.0, 0.25, 1.0 - abs(dot(normalize(vW - cameraPosition), v)));
    vec3 col = vC.rgb * vC.w * prof * along * haze(vW) * uHaze * 0.42;
    col += vC.rgb * vC.w * exp(-vT * 28.0) * prof * 0.6;  // hot near the lens
    gl_FragColor = vec4(col, 1.0);
  }
`;
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const compositeFragment = `
  precision highp float;
  uniform sampler2D uTex;
  uniform sampler2D uNoise;
  uniform sampler2D uZones;
  uniform vec2 uScrNdc;             // the stage screens' centre on screen (ndc)
  uniform float uScrR;              // their size on screen (ndc)
  uniform float uHazeGlow;
  uniform float uTime;
  uniform float uAspect;
  uniform float uVeil;              // the blinders' glare over the whole frame
  uniform vec3 uVeilCol;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uTex, vUv).rgb;
    // a gentle knee: dense crossings never clip to white
    float m = max(c.r, max(c.g, c.b));
    if (m > 0.7) c *= (0.7 + 0.3 * (1.0 - exp(-(m - 0.7) / 0.3))) / m;
    // the haze around the stage, lit by the screens
    vec2 nd = vUv * 2.0 - 1.0;
    float n = texture2D(uNoise, vUv * vec2(uAspect, 1.0) * 0.9 + vec2(uTime * 0.01, uTime * 0.004)).r * texture2D(uNoise, vUv * 1.7 - vec2(uTime * 0.007, 0.0)).r;
    float g = exp(-length((nd - uScrNdc) * vec2(uAspect, 1.0)) / max(uScrR, 0.05));
    vec3 zone = texture2D(uZones, vec2(clamp(0.5 + (nd.x - uScrNdc.x) * 0.35, 0.02, 0.98), 0.5)).rgb;
    c += zone * n * g * uHazeGlow;
    // the blinders' glare: a bloom of light around the top of the screens, not a grey sheet over the frame
    c += uVeilCol * uVeil * exp(-length((nd - uScrNdc - vec2(0.0, uScrR * 0.55)) * vec2(uAspect, 1.0)) * 1.6);
    gl_FragColor = vec4(c, 1.0);
  }
`;
const glareVertex = `
  precision highp float;
  attribute float aHead;
  uniform vec3 uO[${HEADS_MAX}];
  uniform vec4 uC[${HEADS_MAX}];
  uniform vec3 uD[${HEADS_MAX}];
  uniform float uN;
  uniform float uPx;                 // pixels per world unit at unit depth
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec4 vC;
  varying float vSafe;
  void main() {
    int i = int(aHead + 0.5);
    vec3 O = vec3(0.0), D = vec3(0.0); vec4 C = vec4(0.0);
    for (int k = 0; k < ${HEADS_MAX}; k++) if (k == i) { O = uO[k]; C = uC[k]; D = uD[k]; }
    if (aHead >= uN) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 1.0; return; }
    vec4 mv = viewMatrix * vec4(O, 1.0);
    gl_Position = projectionMatrix * mv;
    // brighter when the head looks toward the camera
    float look = max(0.0, dot(D, normalize(cameraPosition - O)));
    vC = vec4(C.rgb, C.w * (0.35 + 0.65 * look * look));
    gl_PointSize = clamp(0.45 * uPx / max(-mv.z, 0.5), 3.0, 36.0);
    vSafe = safeZoneAttenuation(gl_Position);
  }
`;
const glareFragment = `
  precision highp float;
  varying vec4 vC; varying float vSafe;
  void main() {
    vec2 q = gl_PointCoord - 0.5;
    float r = length(q) * 2.0;
    float core = 1.0 - smoothstep(0.12, 0.22, r);
    float halo = exp(-r * 4.0) * 0.35;
    vec3 c = mix(vC.rgb, vec3(1.0), 0.6) * core * 0.9 + vC.rgb * halo;
    gl_FragColor = vec4(c * vC.w * vSafe, 1.0);
  }
`;

export class StageBeams {
  constructor(scene, count, zonesTexture) {
    this._scene = scene;
    this.count = count;
    this._noise = noiseTexture();
    const O = [], D = [], C = [], SP = [];
    for (let i = 0; i < HEADS_MAX; i++) { O.push(new THREE.Vector3()); D.push(new THREE.Vector3(0, -1, 0)); C.push(new THREE.Vector4()); SP.push(0.06); }
    this.heads = { O, D, C, SP };
    // the cones
    const cyl = new THREE.CylinderGeometry(1, 1, 1, 18, 1, true);
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = cyl.index; geo.setAttribute('position', cyl.getAttribute('position'));
    const ah = new Float32Array(HEADS_MAX); for (let i = 0; i < HEADS_MAX; i++) ah[i] = i;
    geo.setAttribute('aHead', new THREE.InstancedBufferAttribute(ah, 1));
    geo.instanceCount = HEADS_MAX;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    this._cyl = cyl; this._geo = geo;
    this.uniforms = { uO: { value: O }, uD: { value: D }, uC: { value: C }, uSpread: { value: SP }, uN: { value: count }, uNoise: { value: this._noise }, uTime: { value: 0 }, uHaze: { value: 1 } };
    this._coneMat = new THREE.ShaderMaterial({ uniforms: this.uniforms, vertexShader: coneVertex, fragmentShader: coneFragment, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    this._coneScene = new THREE.Scene();
    const cones = new THREE.Mesh(geo, this._coneMat); cones.frustumCulled = false; this._coneScene.add(cones);
    // the composite
    this.compU = { uTex: { value: null }, uNoise: { value: this._noise }, uZones: { value: zonesTexture }, uScrNdc: { value: new THREE.Vector2(0, 0.3) }, uScrR: { value: 0.6 }, uHazeGlow: { value: 0.05 }, uTime: this.uniforms.uTime, uAspect: safeZoneUniforms.uAspect, uVeil: { value: 0 }, uVeilCol: { value: new THREE.Vector3(1, 0.95, 0.85) } };
    this._comp = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this.compU, vertexShader: quadVertex, fragmentShader: compositeFragment, transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending }));
    this._comp.frustumCulled = false; this._comp.renderOrder = 10;
    const self = this;
    this._comp.onBeforeRender = function (renderer, s, camera) { self._render(renderer, camera); };
    scene.add(this._comp);
    // the lenses
    const gg = new THREE.BufferGeometry();
    gg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(HEADS_MAX * 3), 3));
    gg.setAttribute('aHead', new THREE.BufferAttribute(ah, 1));
    gg.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    this._glareGeo = gg;
    this.glareU = Object.assign({}, safeZoneUniforms, { uO: { value: O }, uC: { value: C }, uD: { value: D }, uN: this.uniforms.uN, uPx: { value: 600 } });
    this._glare = new THREE.Points(gg, new THREE.ShaderMaterial({ uniforms: this.glareU, vertexShader: glareVertex, fragmentShader: glareFragment, transparent: true, depthTest: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    this._glare.frustumCulled = false; this._glare.renderOrder = 25;
    scene.add(this._glare);
    this._rt = null;
  }
  _render(renderer, camera) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(new THREE.Vector2()); w = b.x; h = b.y; }
    const hw = Math.max(2, Math.round(w / 2)), hh = Math.max(2, Math.round(h / 2));
    if (!this._rt || this._rt.width !== hw || this._rt.height !== hh) {
      if (this._rt) this._rt.dispose();
      this._rt = new THREE.WebGLRenderTarget(hw, hh, { depthBuffer: false, stencilBuffer: false, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    }
    const cc = renderer.getClearColor(new THREE.Color()), ca = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._rt); renderer.clear(true, false, false);
    renderer.render(this._coneScene, camera);
    renderer.setRenderTarget(cur); renderer.setClearColor(cc, ca);
    this.compU.uTex.value = this._rt.texture;
    this.glareU.uPx.value = h / (2 * Math.tan(THREE.MathUtils.degToRad(camera.fov || 50) / 2));
  }
  dispose() {
    this._scene.remove(this._comp); this._scene.remove(this._glare);
    this._comp.onBeforeRender = null;
    this._comp.geometry.dispose(); this._comp.material.dispose();
    this._coneMat.dispose(); this._geo.dispose(); this._cyl.dispose();
    this._glare.material.dispose(); this._glareGeo.dispose();
    this._noise.dispose();
    if (this._rt) this._rt.dispose();
  }
}
